import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCompanyById, addUserToCompany } from '../api/company';
import { getUsers } from '../api/user';
import { UserRole } from '../types/Auth';
import Loader from '../components/Loader';
import ErrorPage from './ErrorPage';

const assignableRoles: UserRole[] = ['admin', 'incidence-manager', 'it-member'];

const CompanyDetail: React.FC = () => {
    const { id: companyId } = useParams<{ id: string }>();
    const queryClient = useQueryClient();

    const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState<UserRole>('it-member');

    const { data: company, isLoading: isLoadingCompany } = useQuery({
        queryKey: ['company', companyId],
        queryFn: () => getCompanyById(companyId!),
        enabled: !!companyId,
    });

    const { data: allUsers, isLoading: isLoadingUsers } = useQuery({ 
        queryKey: ['users'], 
        queryFn: getUsers 
    });

    const addUserMutation = useMutation({
        mutationFn: addUserToCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['company', companyId] });
            setAddUserModalOpen(false);
            toast.success('User added to company successfully!');
        },
        onError: (error: Error) => {
            toast.error(`Failed to add user: ${error.message}`);
        }
    });

    const handleAddUser = (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId || !selectedUserId) return;
        addUserMutation.mutate({ companyId, userId: selectedUserId, role: selectedRole });
    };

    const { usersInCompany, usersNotInCompany } = useMemo(() => {
        if (!allUsers) return { usersInCompany: [], usersNotInCompany: [] };
        const inCompany = allUsers.filter(user => user.company_id === companyId);
        const notInCompany = allUsers.filter(user => !user.company_id);
        return { usersInCompany: inCompany, usersNotInCompany: notInCompany };
    }, [allUsers, companyId]);

    if (isLoadingCompany) return <Loader />;
    if (!company) return <ErrorPage title="Company Not Found" />;

    return (
        <div className="p-6">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-center space-x-4">
                    {company.logo && <img src={company.logo} alt={company.name} className="w-16 h-16 rounded-full object-cover" />}
                    <div>
                        <h1 className="text-3xl font-bold">{company.name}</h1>
                        <p className="text-gray-500">{company.root_domain}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Users</h2>
                    <button onClick={() => setAddUserModalOpen(true)} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700">Add User</button>
                </div>
                {isLoadingUsers ? <Loader /> : (
                    <ul className="divide-y divide-gray-200">
                        {usersInCompany.map(user => (
                            <li key={user.id} className="py-3 flex justify-between items-center">
                                <span>{user.name || user.email}</span>
                                <span className="text-sm text-gray-500">{(user.roles || []).join(', ')}</span>
                            </li>
                        ))}
                        {usersInCompany.length === 0 && <p className="text-center text-gray-500 py-4">No users have been assigned to this company yet.</p>}
                    </ul>
                )}
            </div>

            {isAddUserModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Add User to {company.name}</h2>
                        <form onSubmit={handleAddUser} className="space-y-4">
                            <div>
                                <label htmlFor="user-select" className="block text-sm font-medium text-gray-700">User</label>
                                <select id="user-select" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required>
                                    <option value="" disabled>Select an unassigned user...</option>
                                    {usersNotInCompany.map(user => (
                                        <option key={user.id} value={user.id}>{user.name || user.email}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="role-select" className="block text-sm font-medium text-gray-700">Role</label>
                                <select id="role-select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value as UserRole)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                    {assignableRoles.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-end space-x-2 pt-4">
                                <button type="button" onClick={() => setAddUserModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md" disabled={addUserMutation.isPending}>
                                    {addUserMutation.isPending ? 'Adding User...' : 'Add User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDetail;
